import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api, { formatApiError, API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Briefcase, MapPin, IndianRupee, Building2, ArrowRight, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

function inr(n) { if (n == null) return '—'; return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }

export function PublicJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/public/jobs'); setJobs(data); }
      catch (e) {} finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="public-jobs-page">
      <header className="bg-[#3D4A8E] text-white">
        <div className="max-w-5xl mx-auto px-6 py-12 flex items-center justify-between">
          <Link to="/jobs" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-bold text-xl tracking-tight">LANVAUS</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Careers</div>
            </div>
          </Link>
          <Link to="/login" className="text-sm text-white/70 hover:text-white">HRMS login →</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Open positions</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-2 leading-tight">
          Build the next chapter<br />of Lanvaus with us.
        </h1>
        <p className="text-slate-500 mt-4 max-w-xl">We're looking for curious, kind, and rigorous people. Below are roles we're currently hiring for.</p>

        <div className="mt-10 space-y-4">
          {loading && <div className="text-center py-10 text-slate-400">Loading…</div>}
          {!loading && jobs.length === 0 && <div className="text-center py-16 text-slate-400">No open positions right now. Check back soon.</div>}
          {jobs.map(j => (
            <div key={j.id} data-testid={`public-job-${j.id}`}
                 onClick={() => nav(`/jobs/${j.id}`)}
                 className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#3D4A8E] hover:shadow-md transition-all cursor-pointer flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-xl text-slate-900">{j.title}</div>
                <div className="text-sm text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j.department || '—'}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                  <span>· {j.employment_type}</span>
                  {j.grade && <span>· {j.grade}</span>}
                </div>
                {(j.salary_min || j.salary_max) && (
                  <div className="text-sm text-slate-700 mt-2 flex items-center gap-1">
                    <IndianRupee className="w-3.5 h-3.5" /> {inr(j.salary_min)} – {inr(j.salary_max)} / year
                  </div>
                )}
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </div>
          ))}
        </div>
      </div>
      <footer className="border-t border-slate-200 mt-16 py-8 text-center text-xs text-slate-400">
        &copy; {new Date().getFullYear()} Lanvaus Private Limited
      </footer>
    </div>
  );
}

export function PublicJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', cover_letter: '', linkedin_url: '', resume_file_id: '', resume_name: '' });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/jobs/${id}`); setJob(data); }
      catch (e) { toast.error('Job not found'); nav('/jobs'); }
      finally { setLoading(false); }
    })();
  }, [id, nav]);

  const uploadResume = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/public/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, resume_file_id: data.id, resume_name: data.filename }));
      toast.success('Resume uploaded');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name || !form.email) return toast.error('Name and email required');
    setBusy(true);
    try {
      await api.post('/public/applications', { ...form, job_id: id });
      setSubmitted(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!job) return null;

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="apply-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="font-heading text-3xl font-bold mt-6 text-slate-900">Application received</h1>
          <p className="text-slate-500 mt-2">Thank you for applying to <b>{job.title}</b>. Our recruiting team will reach out within a few working days.</p>
          <Link to="/jobs"><Button className="mt-6 rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">Back to careers</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="public-job-detail">
      <header className="bg-[#3D4A8E] text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/jobs" className="flex items-center gap-2 text-sm text-white/80 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> All openings
          </Link>
          <div className="font-heading font-bold tracking-tight">LANVAUS</div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">{job.department || 'Open position'}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-2">{job.title}</h1>
          <div className="text-sm text-slate-500 flex items-center gap-3 mt-3 flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.location}</span>
            <span>· {job.employment_type}</span>
            {job.grade && <span>· {job.grade}</span>}
            {(job.salary_min || job.salary_max) && <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> {inr(job.salary_min)} – {inr(job.salary_max)}/yr</span>}
          </div>

          <div className="prose prose-slate mt-8">
            <h2 className="font-heading text-xl font-bold">About this role</h2>
            <p className="whitespace-pre-line text-slate-700 leading-relaxed">{job.description}</p>
            {job.requirements && <>
              <h2 className="font-heading text-xl font-bold mt-8">What we're looking for</h2>
              <p className="whitespace-pre-line text-slate-700 leading-relaxed">{job.requirements}</p>
            </>}
          </div>
        </div>

        <aside>
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-6" data-testid="apply-form">
            <h3 className="font-heading text-xl font-bold text-slate-900">Apply now</h3>
            <p className="text-xs text-slate-500 mt-1">Takes 2 minutes.</p>
            <div className="space-y-3 mt-5">
              <div><Label className="text-xs font-semibold text-slate-600">Full name *</Label><Input data-testid="apply-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl mt-1 bg-white" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Email *</Label><Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-xl mt-1 bg-white" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Phone</Label><Input data-testid="apply-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-xl mt-1 bg-white" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">LinkedIn URL</Label><Input data-testid="apply-linkedin" value={form.linkedin_url} onChange={(e) => setForm({...form, linkedin_url: e.target.value})} className="rounded-xl mt-1 bg-white" /></div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Resume (PDF)</Label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="apply-resume"
                       onChange={(e) => uploadResume(e.target.files?.[0])}
                       className="block w-full mt-1 text-xs file:mr-3 file:rounded-full file:border-0 file:bg-[#3D4A8E] file:text-white file:px-3 file:py-1.5 file:text-xs file:font-medium" />
                {uploading && <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</div>}
                {form.resume_name && <div className="text-xs text-emerald-600 mt-1">✓ {form.resume_name}</div>}
              </div>
              <div><Label className="text-xs font-semibold text-slate-600">Cover letter</Label><Textarea data-testid="apply-cover" rows={3} value={form.cover_letter} onChange={(e) => setForm({...form, cover_letter: e.target.value})} className="rounded-xl mt-1 bg-white" /></div>
              <Button onClick={submit} disabled={busy || uploading} data-testid="submit-apply"
                      className="w-full rounded-full h-12 bg-[#3D4A8E] hover:bg-[#5C6BC0] mt-2">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Submit application'}
              </Button>
            </div>
          </div>
        </aside>
      </div>
      <footer className="border-t border-slate-200 mt-16 py-8 text-center text-xs text-slate-400">
        &copy; {new Date().getFullYear()} Lanvaus Private Limited
      </footer>
    </div>
  );
}
